import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Crown, Copy, Plus, ShareNetwork } from '@phosphor-icons/react';

const Invites = () => {
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);
  const [remaining, setRemaining] = useState(user?.invites_remaining ?? 0);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get('/invites/mine');
    setInvites(data.invites || []);
    setRemaining(data.remaining ?? 0);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/invites/generate');
      toast.success(`New invite: ${data.code}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'cannot generate');
    } finally { setBusy(false); }
  };

  const copy = (code) => {
    const url = `${window.location.origin}/register?invite=${code}`;
    navigator.clipboard.writeText(url);
    toast.success('Invite link copied');
  };

  const shareNative = async (code) => {
    const url = `${window.location.origin}/register?invite=${code}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Join BubbleX', text: `You got an invite. Use code ${code}.`, url }); } catch {}
    } else copy(code);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-10 pb-24">
      <div className="text-center mb-10">
        <Crown size={36} weight="duotone" className="text-bx-gold mx-auto" />
        <div className="chip mx-auto mt-3 border-bx-gold/40 text-bx-gold">
          <Crown size={12} weight="fill" /> Founding Carrier · #{user?.founder_number || '—'}
        </div>
        <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter mt-3">
          Your <span className="shimmer-text">invites</span>
        </h1>
        <p className="text-white/65 mt-3 max-w-lg mx-auto">
          You have <span className="text-bx-volt font-bold">{remaining}</span> invites left. They never refill. Use them on people you'd lend your favorite jacket to.
        </p>
      </div>

      <motion.div
        initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="glass rounded-2xl p-6 mb-8 grid grid-cols-3 gap-4 text-center"
      >
        <div>
          <div className="font-display text-3xl font-black text-bx-cyan">{remaining}</div>
          <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">remaining</div>
        </div>
        <div>
          <div className="font-display text-3xl font-black text-bx-volt">{invites.filter(i => i.uses_remaining > 0).length}</div>
          <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">live codes</div>
        </div>
        <div>
          <div className="font-display text-3xl font-black text-bx-magenta">{invites.filter(i => i.used_by).length}</div>
          <div className="text-[10px] uppercase tracking-widest text-white/60 mt-1">accepted</div>
        </div>
      </motion.div>

      <button
        data-testid="invite-generate"
        onClick={generate}
        disabled={busy || remaining <= 0}
        className="w-full btn-primary py-4 rounded-full font-bold mb-6 flex items-center justify-center gap-2 disabled:opacity-40"
      >
        <Plus size={16} weight="bold" /> {busy ? 'minting…' : 'Forge a new invite code'}
      </button>

      <div className="space-y-3">
        {invites.length === 0 && (
          <div className="text-center text-white/60 py-10 text-sm">No invites minted yet. Forge one above.</div>
        )}
        {invites.map((inv) => (
          <div key={inv.code} className="glass-hi rounded-xl p-4 flex items-center gap-3" data-testid={`invite-${inv.code}`}>
            <code className="font-mono font-bold text-lg text-bx-cyan flex-1">{inv.code}</code>
            <span className={`text-xs ${inv.used_by ? 'text-bx-magenta' : 'text-bx-volt'}`}>
              {inv.used_by ? 'accepted' : 'live'}
            </span>
            <button onClick={() => copy(inv.code)} data-testid={`invite-copy-${inv.code}`}
                    className="p-2 rounded-full glass-hi text-white/70 hover:text-white">
              <Copy size={14} weight="bold" />
            </button>
            <button onClick={() => shareNative(inv.code)} data-testid={`invite-share-${inv.code}`}
                    className="p-2 rounded-full glass-hi text-white/70 hover:text-white">
              <ShareNetwork size={14} weight="bold" />
            </button>
          </div>
        ))}
      </div>

      <div className="grad-border p-6 mt-10 text-center">
        <div className="text-[10px] uppercase tracking-widest text-bx-gold">why so few?</div>
        <p className="text-white/70 mt-2 text-sm max-w-md mx-auto">
          BubbleX is invite-only by design. Scarcity means the people who get in actually carry the energy.
          You're a Founding Carrier — your invites matter.
        </p>
      </div>
    </div>
  );
};

export default Invites;
